import { useEffect, useState } from 'react';

import {
    Box,
    Button,
    Card,
    Divider,
    Flex,
    Heading,
    Icon,
    Image,
    Link,
    List,
    ListItem,
    Text,
    UnorderedList,
    useBreakpointValue
} from '@chakra-ui/react';

import moment from 'moment';
import 'moment-timezone';

import { DonationDisplay } from './Misc/DonationDisplay';
import { SetupBox } from './Misc/SetupBox';

import { FaUserEdit, FaMusic } from "react-icons/fa";
import { FaRegCalendar, FaRegCalendarPlus } from "react-icons/fa6";
import { FiExternalLink } from "react-icons/fi";
import { TbLocation } from "react-icons/tb";

export function EventInfo({eventConfig, sessionActive, slotsRemaining, changePage, setups, regTotal, regAllowed, schedAllowed, songdataAllowed}) {

    const [SetupGrid, setSetupGrid] = useState([]);
    const [eventStartDayStr, setEventStartDayStr] = useState("");
    const [eventEndDayStr, setEventEndDayStr] = useState("");
    const [eventYear, setEventYear] = useState("");
    const [eventTimeItems, setEventTimeItems] = useState([]);

    const srpgNum = eventConfig["srpgNum"];
    const eventShort = eventConfig["eventShort"];
    const liveLong = eventConfig["liveLong"];
    const eventOver = eventConfig["eventOver"];
    const doubleSlotCutoff = eventConfig["doubleSlotCutoff"];

    const regButtonShown = !eventOver && sessionActive & (regAllowed);
    const schedButtonShown = !eventOver && sessionActive & (schedAllowed);

    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );

    const SignUpDisplay = () => {
        if (!eventOver && (!sessionActive || regButtonShown || schedButtonShown)) {
            return (
                <Card padding={isMobile()? 3 : 8} mb={4}>   
                    <Heading size="md" mb={4}>
                        How do I sign up for {liveLong}?
                    </Heading>
                    <Divider mb={4}/>                                     
                    <SchedButtonsText/>
                    <Box textAlign="center">
                        <RegisterButton/>
                        <ScheduleButton/>
                    </Box>
                </Card>
            );
        }
        return ([]);
    }

    const SongDataDisplay = () => {
        if (songdataAllowed) {
            return (
                <>
                    <Text mb={4}>
                        You can view the songlist and point values on the Song Data page:
                    </Text>
                    <Flex>
                        <Button ml="auto" mr="auto" mb={4} width={{base: "250px", xl: "600px"}} onClick={()=>{changePage("song-data")}}>
                            <Icon as={FaMusic} boxSize={5} mr={2}/> Song Data
                        </Button>
                    </Flex>     
                </>
            );
        }
        return (
            <>
                <Text mb={4}>
                    TPX and LPX point values for the charts will be available at a later date.
                </Text>
            </>
        )
    }

    const SchedButtonsText = () => {
        if(sessionActive) {
            if(regButtonShown) {
                return (
                    <Card bgColor="whiteAlpha.300" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Registration
                        </Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            If you want to compete, you can register and then schedule a set-- or if you want to just come to hang out, you can attend as a 
                            spectator.
                        </Text>
                    </Card>
                );
            }
            if(schedButtonShown) {
                let slotStr = "a set"

                if (slotsRemaining > 1) {
                    slotStr = `${slotsRemaining} sets`
                }

                return (
                    <Card bgColor="whiteAlpha.300" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Set Scheduling
                        </Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            Now that you've registered as a competitor, you can schedule {slotStr}.
                        </Text>
                    </Card>
                );
            }            
            return([]);
        }
        return (
            <Card bgColor="whiteAlpha.300" padding={isMobile()? 3 : 8} mb={4}>
                <Heading size="md" mb={4}>
                    Registration
                </Heading>
                <Divider mb={4}/>
                <Text mb={4}>
                    In order to sign up to attend, you'll need to do the following:
                </Text>
                <UnorderedList ml={isMobile() ? 8 : 12} mb={4}>
                    <ListItem><Link color='teal.300' href={`https://groovestats.com/index.php?page=register`} isExternal>Register for a GrooveStats account <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/></Link></ListItem>
                    <ListItem><Link color='teal.300' onClick={()=>{changePage("log-in")}}>Log in on this website</Link></ListItem>
                    <ListItem>You can then choose to schedule a time slot for your set or register as a spectator</ListItem>
                </UnorderedList>
            </Card>
        );
    };

    const RegisterButton = () => {
        if(regButtonShown) {
            return (
                <Button width={{base: "250px", xl: "600px"}} isDisabled={!regAllowed} flex="1" onClick={()=>{changePage("register")}}>
                    <Icon as={FaUserEdit} boxSize={5} mr={2}/> Register For Event
                </Button>
            );
        }
        return([]);
    }

    const ScheduleButton = () => {
        if(schedButtonShown) {
            return (
                <Button width={{base: "250px", xl: "600px"}} isDisabled={!schedAllowed} flex="1" onClick={()=>{changePage("sched-set")}}>
                    <Icon as={FaRegCalendarPlus} boxSize={5} mr={2}/> Schedule Set
                </Button>
            );
        }
        return([]);
    }    

    useEffect(()=>{
        const newSetupGrid = Object.values(setups)?.map((setupId, idx)=>
            <SetupBox
                setups={setupId} 
                key={idx}
            />
        );
        setSetupGrid(newSetupGrid);
    }, [setups]);

    useEffect(()=>{
        const eventDates = eventConfig?.eventDates;

        const newEventTimes = [];

        eventDates.forEach((eventDate, idx)=>{
            const startDateTime = moment.tz(`${eventDate["date"]} ${eventDate["start"]}`, "America/New_York");
            const endDateTime = moment.tz(`${eventDate["date"]} ${eventDate["end"]}`, "America/New_York");

            const startWeekday = startDateTime.format("dddd");

            const startTime = startDateTime.format("h:mm a");
            const endTime = endDateTime.format("h:mm a");

            if (idx === 0) {
                const newEventStartDayStr = startDateTime.format("MMMM Do");
                const newEventYear = startDateTime.format("YYYY");
                setEventStartDayStr(newEventStartDayStr);
                setEventYear(newEventYear);
            }

            if (idx === (eventDates.length -1)) {
                const newEventEndDayStr = endDateTime.format("MMMM Do");
                setEventEndDayStr(newEventEndDayStr);
            }

            newEventTimes.push({
                startWeekday: startWeekday,
                startTime: startTime,
                endTime: endTime
            })
        });

        const newTimeListItems = newEventTimes?.map((time, idx)=>
            <ListItem key={idx}><strong>{time.startWeekday}:</strong> {time.startTime} - {time.endTime}</ListItem>
        );

        setEventTimeItems(newTimeListItems);
    }, [eventConfig]);

    try {
        return (
            <Box width="100%">
                <Text mb={4}>
                    Join us for a weekend of stamina, friendship, and fun diversions on the side at {liveLong}!
                </Text>
                <Flex flexFlow="wrap" justifyContent="center" flexDirection="row" gap={3}>
                    <Card flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            When is {liveLong}?
                        </Heading>
                        <Divider mb={4}/>
                        <Heading color="blue.300" size="md" mb={4}>
                            {eventStartDayStr}-{eventEndDayStr} {eventYear}
                        </Heading>
                        <Text mb={4}>
                            The space will open the following hours:
                        </Text>
                        <List>
                            {eventTimeItems}
                        </List>
                    </Card>
                    <Card flex="2" flexBasis="375px" padding={isMobile()? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Where is {liveLong}?
                        </Heading>
                        <Divider mb={4}/>
                        <Heading color="blue.300" size="md" mb={4}>
                            PGH Dance Game Space<br/>
                            Pittsburgh, PA
                        </Heading>
                        <Text mb={4}>
                            You can find more information on the venue by visiting the venue info page:
                        </Text>
                        <Flex>
                            <Button flex="1" mb={4} onClick={()=>{changePage("venue-info")}}>
                                <Icon as={TbLocation} boxSize={5} mr={2}/> Venue Info
                            </Button>
                        </Flex>
                    </Card>
                </Flex>
                <SignUpDisplay/>                
                <Card padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        What is {liveLong}?
                    </Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>Here's what's going on at <strong>{liveLong}</strong>:</Text>
                    <Flex flexFlow="wrap" justifyContent="center" flexDirection="row" gap={3}>
                        <Card bgColor="whiteAlpha.300" flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                            <Heading size="md" mb={4}>
                                Main Tournaments:
                            </Heading>
                            <Divider mb={4}/>                            
                            <UnorderedList mb={4} style={{fontWeight: "bold"}} ml={isMobile() ? 4 : 12}>
                                <ListItem><Heading color="blue.300" size="md">ITG Singles</Heading></ListItem>
                                <ListItem><Heading color="blue.300" size="md">ITG Doubles</Heading></ListItem>
                                <ListItem><Heading color="blue.300" size="md">Pump Singles</Heading></ListItem>
                                <ListItem><Heading color="blue.300" size="md">Pump Doubles</Heading></ListItem>
                            </UnorderedList>    
                            <Text mb={4}>
                                {liveLong} centers around a competition using the songlist from Stamina RPG {srpgNum}.  Entrants will be aiming to
                                get highest sum of TPX and LPX they can.  Only your <strong>best LPX pass</strong> and <strong>five best TPX passes</strong> contribute towards
                                your total-- imagine TP/LP from SRPG, but with scoring as a component.  
                            </Text>
                            <Text mb={4}>Charts converted from ITG Singles will be used for ITG Doubles and Pump Singles/Doubles.</Text>     
                            <SongDataDisplay/>
                            <Text mb={4}>
                                ... and you can read up on Stamina RPG {srpgNum} here:
                            </Text>
                            <Text mb={4}>
                                <Link color='teal.300' href={`https://${eventShort}.groovestats.com/`} isExternal>
                                    Stamina RPG {srpgNum} Info/Rules <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/>
                                </Link>
                            </Text>        
                            <Heading size="md" mb={4}>
                                Prizes:
                            </Heading>
                            <Divider mb={4}/>                                                              
                            <Text mb={4}>
                                Various leaderboards across the main tournaments will be used to determine prizes to be awarded.
                            </Text>                                                      
                            <Heading color="blue.200" size="sm">
                                Overall Best:
                            </Heading>          
                            <Text mb={4}>
                                A ranking of each player's highest point total out of the four different tournaments.
                            </Text>
                            <UnorderedList ml={isMobile() ? 4 : 6}  mb={4}>
                                <ListItem><strong>Overall Best 1st Place:</strong> Ribbon + Trophy + SC3 Travel Pad + $500</ListItem>
                                <ListItem><strong>Overall Best 2nd Place:</strong> Trophy + $250</ListItem>
                                <ListItem><strong>Overall Best 3rd Place:</strong> Trophy + $125</ListItem>
                                <ListItem><strong>Overall Best 4th Place:</strong> Trophy + $75</ListItem>
                            </UnorderedList>
                            <Heading color="blue.200" size="sm">
                                Combined Total:
                            </Heading>
                            <Text mb={4}>
                                This ranking is based on the sum of your points across all four tournaments.
                            </Text>                                                      
                            <UnorderedList ml={isMobile() ? 2 : 6} mb={4}>
                                <ListItem><strong>Combined 1st Place:</strong> Ribbon + Trophy</ListItem>
                            </UnorderedList>
                            <Heading color="blue.200" size="sm">
                                Individual Wins:
                            </Heading>
                            <Text mb={4}>
                                Prizes awarded for placement in the individual tournaments.
                            </Text>                                                      
                            <UnorderedList ml={isMobile() ? 4 : 6} mb={4}>
                                <ListItem><strong>ITG Singles 1st Place:</strong> Ribbon + Trophy</ListItem>
                                <ListItem><strong>ITG Doubles 1st Place:</strong> Ribbon + Trophy</ListItem>
                                <ListItem><strong>Pump Singles 1st Place:</strong> Ribbon + Trophy</ListItem>
                                <ListItem><strong>Pump Doubles 1st Place:</strong> Ribbon + Trophy</ListItem>
                            </UnorderedList>      
                            <Text mb={4}>
                                Here's what the <strong>Overall Best 1st Place</strong> travel pad prize looks like-- it's a design that can be disassembled
                                into multiple parts and stored in your luggage:
                            </Text>    
                            <Image borderRadius={5} ml="auto" mr="auto" maxWidth={{base: "300px", xl: "400px"}} src={process.env.PUBLIC_URL + '/images/sc3-pad-prize.jpg'} />                                
                            <Text mb={4}>
                                Special thanks to Perusal and JOKR, who are donating a custom travel pad as a prize!           
                            </Text>                   
                            <Divider mb={4}/>                
                            <Text mb={4}>
                                Keep reading below to learn more about set scheduling and the machine setups that will be available
                                for the main tournaments!
                            </Text>

                        </Card>

                        <Card bgColor="whiteAlpha.300" flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                            <Heading size="md" mb={4}>
                                Side Events:
                            </Heading>
                            <Divider mb={4}/>                                  
                            <UnorderedList ml={isMobile() ? 4 : 8}>
                                <ListItem>
                                    <Heading color="blue.300" size="sm">DAILY SIDE EVENTS & QUESTS</Heading> 
                                    <UnorderedList ml={isMobile() ? 2 : 6}>
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Friday: Pin the Tail (Stamina Edition)
                                            </Heading>
                                            <Text mb={2}>
                                                No donkeys involved here... probably squirrels.
                                            </Text>
                                        </ListItem>
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Saturday: Mummy Wrap Challenge
                                            </Heading>
                                            <Text mb={2}>
                                                Grab a teammate and wrap them in toilet paper in under 2 minutes. The most "mummyesque" pair wins!
                                            </Text>
                                        </ListItem>
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Sunday: Pumpkin Painting
                                            </Heading>
                                            <Text mb={2}>
                                                Show off your artistic skills! Pumpkins and acrylic markers provided. 
                                            </Text>
                                            <Text mb={2}>
                                                Pumpkins judged for:
                                            </Text>
                                            <UnorderedList ml={isMobile() ? 4 : 6}>
                                                <ListItem>Aesthetic & Skill</ListItem>
                                                <ListItem>Seasonality (Fall / Halloween vibes)</ListItem>
                                                <ListItem>Stamina Spirit</ListItem>
                                            </UnorderedList>
                                        </ListItem>                                            
                                    </UnorderedList>
                                </ListItem> 

                                <ListItem>
                                    <Heading color="blue.300" size="sm">THEME DAYS</Heading> 
                                    <Text mb={2}>
                                        Dress to the daily theme to get a spin on the Prize Wheel, wearing shirts with imagery counts, no full costumes are needed.
                                    </Text>
                                    <UnorderedList ml={isMobile() ? 4 : 6}>
                                        <ListItem><strong>Friday:</strong> Halloween & Spooky</ListItem>
                                        <ListItem><strong>Saturday:</strong> Stamina Con 3 Shirts</ListItem>
                                        <ListItem><strong>Sunday:</strong> ECS & Stamina RPG Shirts</ListItem>
                                    </UnorderedList>
                                </ListItem>                

                                <ListItem>
                                    <Heading color="blue.300" size="sm">ALL-WEEKEND EVENTS & QUESTS</Heading> 
                                    <UnorderedList ml={isMobile() ? 4 : 6}>
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Scavenger Hunt Trivia
                                            </Heading>
                                            <Text mb={2}>
                                                Find the SC3 logos hidden throughout the PGH space. Scan the code, answer the 4 questions, and stack up points! The attendee with the most points by the end of the weekend wins.
                                            </Text>
                                            <UnorderedList ml={isMobile() ? 4 : 6}>
                                                <ListItem><strong>Scan only:</strong> 1 Pt</ListItem>
                                                <ListItem><strong>Scan + 1 correct answer:</strong> 2 Pts</ListItem>
                                                <ListItem><strong>Scan + 2 correct answers:</strong> 3 Pts</ListItem>
                                                <ListItem><strong>Scan + 3 correct answers:</strong> 4 Pts</ListItem>
                                                <ListItem><strong>Scan + All 4 correct answers:</strong> 5 Pts</ListItem>
                                            </UnorderedList>                                                
                                        </ListItem>
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Door Guard Duty
                                            </Heading>
                                            <Text mb={2}>
                                                Help keep things running smoothly! Grab a punch card and get a punch every time you assist:
                                            </Text>        
                                            <UnorderedList ml={isMobile() ? 4 : 6}>
                                                <ListItem><strong>5 Punches:</strong> Spin the Prize Wheel!</ListItem>
                                                <ListItem><strong>10 Punches:</strong> Spin the Prize Wheel + Earn the Stairs Hero Ribbon!</ListItem>
                                            </UnorderedList>                                                                                           
                                        </ListItem>
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Cozy Lounge & Gaming
                                            </Heading>
                                            <Text mb={2}>
                                                Bring your gear, trade cards, and hang out with fellow attendees!
                                            </Text>
                                        </ListItem>
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Cozy Lounge & Gaming
                                            </Heading>
                                            <Text mb={2}>
                                                Bring your gear, trade cards, and hang out with fellow attendees!
                                            </Text>
                                            <UnorderedList ml={isMobile() ? 4 : 6}>
                                                <ListItem><strong>Pokémon & TCG:</strong> Bring your cards, Switch, or 3DS to trade and battle!</ListItem>
                                                <ListItem><strong>Cozy & Casual:</strong> Animal Crossing, Board Games, and more.</ListItem>
                                            </UnorderedList>                                                  
                                        </ListItem>    
                                        <ListItem>
                                            <Heading color="blue.200" size="xs">
                                                Group Photo
                                            </Heading>
                                            <Text mb={2}>
                                                I'm just putting this one here so that people are aware it's happening!
                                            </Text>
                                        </ListItem>                                                                                    
                                    </UnorderedList>
                                </ListItem>      
                            </UnorderedList>                                                       
                            <Heading size="md" mb={4}>
                                Prizes:
                            </Heading>
                            <Divider mb={4}/>                                 
                            <UnorderedList ml={isMobile() ? 4 : 8}>
                                <ListItem>
                                    <Text mb={2}>
                                        Win any side event to claim an Event Ribbon and spin the Prize Wheel!
                                    </Text>
                                    <Heading color="blue.200" size="xs">Prize Wheel Rewards:</Heading>
                                    <UnorderedList ml={isMobile() ? 4 : 6}>
                                        <ListItem>Stamina Con 3 Towel (Only 20 available!)</ListItem>
                                        <ListItem>Stamina Con 3 Stickers</ListItem>
                                        <ListItem>Stamina Con 3 Mouse Pads (Only 20 available!)</ListItem>
                                        <ListItem>Candy</ListItem>
                                    </UnorderedList>
                                </ListItem>
                            </UnorderedList>                    
                        </Card>                    
                    </Flex> 
                </Card>                                       
                <Card flex="2" flexBasis="375px" padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Tournament Setups and Scheduling
                    </Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>
                        At the event, if you're competing, you have the opportunity to select a setup to do your set(s) on.
                        These mostly include DDR/ITG pads, and if you select a setup where
                        you're allowed to swap the pads out, you can bring whatever full sized pad you want.
                    </Text>
                    <Text mb={4}>
                        For <strong>travel/mini pads only</strong> (please check with us on 
                        <Link color='teal.300' href={'https://staminanation.com/'} isExternal> Discord <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/></Link> 
                        to make sure yours counts), you may bring and use the pad on <em>any</em> setup-- including the 'non-swappable' ones--
                        provided you bring a non-slip mat to place between your pad and the surface of the setup's default pad to avoid 
                        any damage.
                    </Text>
                    <Text mb={4}>
                        Note that after you've checked in at the event, <strong>scores obtained at any time on any setup will count towards your total</strong>.
                        However, during your set(s), you have guaranteed access to the setup you chose, so it is to your advantage to schedule one.
                    </Text>
                    <Text mb={4}>
                        Here are details on the different options for setups:
                    </Text>
                    <Flex mt={3} flexDirection="row" flexFlow="wrap" justifyContent={"center"}>
                        {SetupGrid}    
                    </Flex>
                    <Text mb={4}>
                        All entrants are allowed a 3 hour block on the schedule for their sets.  Additionally, anyone 
                        entering that meets or exceeds <strong>{doubleSlotCutoff}</strong> TP+LP from SRPG{srpgNum} can 
                        register a <strong>SECOND</strong> time slot.
                    </Text>                    
                    <Text mb={4}>
                        Outside of the core time slots for competing, the machines will be free to use for
                        anyone who wants to play.
                    </Text>
                    <Text mb={4}>
                        You can see what times the various entrants are playing by checking the calendar below:
                    </Text>
                    <Box textAlign="center">
                        <Button width={{base: "250px", xl: "600px"}} onClick={()=>{changePage("calendar")}}>
                            <Icon as={FaRegCalendar} boxSize={5} mr={2}/> Set Calendar/Schedule
                        </Button>
                    </Box>
                </Card>
                <Card padding={isMobile() ? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Who/Why is {liveLong}?
                    </Heading>
                    <Divider mb={4}/>
                    <Box alignContent="space-around">
                        <Heading align="center" size="md" mb={4}>
                            Currently,
                        </Heading>
                        <Heading align="center" size="xl" mb={4}>{regTotal} / {eventConfig["maxPlayerCount"]}</Heading>
                        <Heading align="center" size="md" mb={4}>
                            registration slots have been filled.
                        </Heading>
                    </Box>                    
                    <Text mb={4}>
                        I want to give special mention to the following people for contributing to this event and helping to make it possible:
                    </Text>
                    <UnorderedList ml={isMobile() ? 8 : 12}>
                        <ListItem><strong>The PGH Dance Game Space Staff - </strong> for allowing us to return this year and host more events!</ListItem>
                        <ListItem><strong>Xiwangz - </strong> for helping me plan and run the event!</ListItem>
                        <ListItem><strong>quietly-turning - </strong> for building the PGH Dance Game Space originally, and helping us get started running events here last year!  And a million other things!</ListItem>
                        <ListItem><strong>teejusb - </strong> for making the ITGmania theme for the tournament!</ListItem>
                        <ListItem><strong>Pluto - </strong> for the neat logo design!</ListItem>                  
                        <ListItem>... and <strong>everyone who registered to come - </strong> we can't run an event like this if people don't want to attend, so thank you for being a part of everything!</ListItem>
                    </UnorderedList>                    
                </Card>
                <Card padding={isMobile() ? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Contributing
                    </Heading>     
                    <Divider mb={4}/>                                           
                    <DonationDisplay/>
                </Card>

            </Box>
        );
    } catch (e) {
        console.error("EventInfo render error: ", e);
    }
}